/**
 * ValidationChecklist — renders a validation result's checks with
 * pass/warn/fail icons and detail text. Accepts the engine's
 * { checks: [{id, level, detail, paths?}] } shape (or a pre-filtered list).
 */
import React, { useState } from 'react';
import {
  Box,
  HStack,
  Text,
  Icon,
  Button,
  useColorModeValue,
} from '@chakra-ui/react';
import { FiCheckCircle, FiAlertTriangle, FiXCircle } from 'react-icons/fi';

const LEVEL = {
  pass: { icon: FiCheckCircle, color: 'green.500' },
  warn: { icon: FiAlertTriangle, color: 'yellow.500' },
  fail: { icon: FiXCircle, color: 'red.500' },
};

export default function ValidationChecklist({ checks = [], showPasses = false }) {
  const [expanded, setExpanded] = useState(showPasses);
  const detailColor = useColorModeValue('gray.600', 'gray.400');

  const notPass = checks.filter((c) => c.level !== 'pass');
  const passes = checks.filter((c) => c.level === 'pass');
  const visible = expanded ? [...notPass, ...passes] : notPass;

  if (!checks.length) {
    return (
      <Text fontSize="sm" color={detailColor}>
        No validation checks recorded.
      </Text>
    );
  }

  return (
    <Box>
      {!notPass.length && (
        <HStack spacing={2} mb={2}>
          <Icon as={FiCheckCircle} color="green.500" />
          <Text fontSize="sm" fontWeight="medium">
            All {checks.length} checks pass
          </Text>
        </HStack>
      )}
      {visible.map((check, i) => {
        const lv = LEVEL[check.level] || LEVEL.warn;
        return (
          <HStack key={`${check.id}-${i}`} align="flex-start" spacing={2} py={1}>
            <Icon as={lv.icon} color={lv.color} mt="3px" flexShrink={0} />
            <Box minW={0}>
              <Text fontSize="sm" fontWeight="medium" fontFamily="mono">
                {check.id}
              </Text>
              {check.detail && (
                <Text fontSize="xs" color={detailColor} whiteSpace="pre-wrap">
                  {check.detail}
                </Text>
              )}
            </Box>
          </HStack>
        );
      })}
      {passes.length > 0 && notPass.length > 0 && (
        <Button size="xs" variant="link" mt={1} onClick={() => setExpanded((v) => !v)}>
          {expanded ? 'Hide' : 'Show'} {passes.length} passing check{passes.length === 1 ? '' : 's'}
        </Button>
      )}
    </Box>
  );
}
